<template>
  <div class="control-panel space-y-4">
    <!-- Horizontal filter row -->
    <div class="filter-row overflow-x-auto">
      <!-- Operator selector -->
      <div class="filter-item">
        <label class="label">Operator</label>
        <DxSelectBox
          :items="operatorOptions"
          display-expr="operatorName"
          value-expr="operatorId"
          v-model:value="selectedOperator"
          placeholder="Select Operator"
          :disabled="operatorOptions.length === 0"
          styling-mode="outlined"
          :width="180"
        />
      </div>
      <!-- Chemical selector -->
      <div class="filter-item">
        <label class="label">Chemical</label>
        <DxSelectBox
          :items="chemicalOptions"
          v-model:value="selectedChemical"
          placeholder="Select Chemical"
          :disabled="!selectedOperator || chemicalOptions.length === 0"
          styling-mode="outlined"
          :width="180"
        />
      </div>

      <!-- District selector -->
      <div class="filter-item">
        <label class="label">District</label>
        <DxSelectBox
          :items="districtOptions"
          display-expr="name"
          value-expr="id"
          v-model:value="selectedDistrict"
          placeholder="All Districts"
          :disabled="!selectedOperator"
          :show-clear-button="true"
          styling-mode="outlined"
          :width="180"
        />
      </div>

      <!-- Office selector -->
      <div class="filter-item">
        <label class="label">Office</label>
        <DxSelectBox
          :items="officeOptions"
          display-expr="name"
          value-expr="id"
          v-model:value="selectedOffice"
          placeholder="All Offices"
          :disabled="!selectedOperator"
          :show-clear-button="true"
          styling-mode="outlined"
          :width="180"
        />
      </div>

      <!-- Area selector -->
      <div class="filter-item">
        <label class="label">Area</label>
        <DxSelectBox
          :items="areaOptions"
          display-expr="name"
          value-expr="id"
          v-model:value="selectedArea"
          placeholder="All Areas"
          :disabled="!selectedOperator"
          :show-clear-button="true"
          styling-mode="outlined"
          :width="180"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineEmits */
import { watch, computed, onMounted } from 'vue';
import DxSelectBox from 'devextreme-vue/select-box';

import filtersState, {
  operators as operatorOptions,
  chemicals as chemicalOptions,
  districts as districtOptions,
  offices   as officeOptions,
  areas     as areaOptions,
  setOperator,
  setChemical,
  setScope,
  ensureOperatorsLoaded,
} from './temp-data-and-supports/useForecastFilters.js';



const emit = defineEmits(['filters-change']);

const selectedOperator = computed({
  get: () => filtersState.operatorId,
  set: val => setOperator(val),
});

const selectedChemical = computed({
  get: () => filtersState.chemical,
  set: val => setChemical(val),
});

// --- Scope Selection Computed Props (Mutually Exclusive) ---

const selectedDistrict = computed({
  get: () => (filtersState.selectedScope.type === 'district' ? filtersState.selectedScope.id : null),
  set: val => setScope(val ? 'district' : 'operator', val),
});

const selectedOffice = computed({
  get: () => (filtersState.selectedScope.type === 'office' ? filtersState.selectedScope.id : null),
  set: val => setScope(val ? 'office' : 'operator', val),
});

const selectedArea = computed({
  get: () => (filtersState.selectedScope.type === 'area' ? filtersState.selectedScope.id : null),
  set: val => setScope(val ? 'area' : 'operator', val),
});

/* --- emit combined payload whenever a selection changes --- */
watch(
  () => [filtersState.operatorId, filtersState.chemical, filtersState.selectedScope],
  ([opId, chem, scope]) => {
    if (opId && chem) {
      emit('filters-change', {
        operatorId: opId,
        chemical: chem,
        scopeType: scope.type === 'operator' ? null : scope.type,
        scopeId: scope.id,
      });
    }
  }
);

// Ensure operators are fetched on mount so the dropdown is populated
onMounted(() => {
  ensureOperatorsLoaded();
});
</script>

<style scoped>

.control-panel {
  padding: 1rem;            /* matches Tailwind p-4 */
  background-color: rgba(255, 255, 255, 0.05); /* bg-white/5 */
  border-radius: 0.75rem;   /* rounded-xl */
  backdrop-filter: blur(8px); /* backdrop-blur */
  margin-bottom: 1rem; /* 16-px spacing below control panel */
}

.filter-item {
  display: flex;
  flex-direction: column;
  flex: 1 1 180px;       /* allow shrink but prefer 180 */
  min-width: 160px;
  max-width: 180px;
}

.label {
  margin-bottom: 0.25rem; /* mb-1 */
  font-size: 0.75rem;     /* text-xs */
  color: rgb(209, 213, 219); /* text-gray-300 */
  text-transform: uppercase;
  letter-spacing: 0.05em; /* tracking-wide */
}

/* Ensure row stays horizontal even if Tailwind flex overridden somewhere */
.filter-row {
  display: flex !important;
  flex-wrap: wrap;
  gap: 1rem; /* matches Tailwind gap-4 */
  max-width: 100%; /* prevent the row from widening its parent */
}

/* Override DevExtreme default width so widget fills the 160-px wrapper but never forces larger width */
.filter-item :deep(.dx-selectbox),
.filter-item :deep(.dx-texteditor),
.filter-item :deep(.dx-tagbox) {
  width: 100% !important;   /* fill 160-px parent */
  min-width: 0 !important;  /* cancel DevExtreme 200-px min */
}
</style>
